/**
 * Date utility functions for consistent timezone handling
 * All dates from backend should be in UTC ISO format
 * These functions convert to user's local timezone for display
 */

/**
 * Get user's timezone from browser
 */
export const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };
  
  /**
   * Parse UTC ISO string and return Date object
   * Handles both 'Z' suffix and '+00:00' format
   */
  export const parseUTCDate = (dateString: string | null | undefined): Date | null => {
    if (!dateString) return null;
  
    const date = new Date(dateString);
  
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return null;
    }
  
    return date;
  };
  
  /**
   * Format date for message timestamps
   * Shows: "HH:MM" for today, "Yesterday HH:MM" for yesterday, "MMM DD HH:MM" for older
   */
  export const formatMessageTime = (dateString: string | null | undefined): string => {
    const date = parseUTCDate(dateString);
    if (!date) return '';
  
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
  
    if (messageDate.getTime() === today.getTime()) {
      // Today - show only time
      return date.toLocaleTimeString([], timeOptions);
    } else if (messageDate.getTime() === yesterday.getTime()) {
      // Yesterday
      return `Yesterday ${date.toLocaleTimeString([], timeOptions)}`;
    } else if (messageDate.getFullYear() === now.getFullYear()) {
      // This year - show date without year
      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      }) + ` ${date.toLocaleTimeString([], timeOptions)}`;
    } else {
      // Different year - show full date
      return date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) + ` ${date.toLocaleTimeString([], timeOptions)}`;
    }
  };
  
  /**
   * Format date only (no time)
   * Format: "MMM DD, YYYY"
   */
  export const formatDate = (dateString: string | null | undefined): string => {
    const date = parseUTCDate(dateString);
    if (!date) return '';
  
    return date.toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  /**
   * Format date and time
   * Format: "MMM DD, YYYY at HH:MM AM/PM"
   */
  export const formatDateTime = (dateString: string | null | undefined): string => {
    const date = parseUTCDate(dateString);
    if (!date) return '';
  
    return date.toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) + ` at ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })}`;
  };
  
  /**
   * Format relative time (e.g., "2 hours ago", "3 days ago")
   */
  export const formatRelativeTime = (dateString: string | null | undefined): string => {
    const date = parseUTCDate(dateString);
    if (!date) return '';
  
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);
  
    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    if (diffWeek < 4) return `${diffWeek} week${diffWeek > 1 ? 's' : ''} ago`;
    if (diffMonth < 12) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
    return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
  };
  
  /**
   * Format for conversation list (smart format based on age)
   */
  export const formatConversationTime = (dateString: string | null | undefined): string => {
    const date = parseUTCDate(dateString);
    if (!date) return '';
  
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  
    if (diffDays === 0) {
      // Today - show time only
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      // This week - show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else if (date.getFullYear() === now.getFullYear()) {
      // This year - show date without year
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else {
      // Different year
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };